import { FC } from 'react';

import {
  Bell,
  ChakraBox,
  ChakraFlex,
  Edit,
  Email,
  EquipUIFireflyV2Theme,
  IconButton,
} from '@equip.health/ui';

import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { shouldShowEditOption } from '~/lib/util/survey.util';

import PrimaryCTAButton from './PrimaryCTAButton';
import SecondaryCTAButton from './SecondaryCTAButton';

const { primary } = EquipUIFireflyV2Theme.colors;

type CTAProps = {
  isDisabled: boolean;
  onClick: () => void;
  survey?: Partial<SurveyResponse>;
};

const StatusToPrimaryCTAMapper: Record<SurveyResponseStatus, FC<CTAProps>> = {
  [SurveyResponseStatus.NEW]: ({ isDisabled, onClick }) => (
    <PrimaryCTAButton
      display="Begin now"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.REQUESTED]: ({ isDisabled, onClick }) => (
    <PrimaryCTAButton
      display="Begin now"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.INCOMPLETE]: ({ isDisabled, onClick }) => (
    <PrimaryCTAButton
      display="View results"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.IN_PROGRESS]: ({ isDisabled, onClick }) => (
    <PrimaryCTAButton
      display="Continue survey"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.DONE]: ({ isDisabled, onClick }) => (
    <PrimaryCTAButton
      display="View results"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.VOID]: () => null,
};

const StatusToSecondaryCTAMapper: Record<SurveyResponseStatus, FC<CTAProps>> = {
  [SurveyResponseStatus.NEW]: ({ isDisabled, onClick }) => (
    <SecondaryCTAButton
      display="Send via email"
      isDisabled={isDisabled}
      leftIcon={<Email fill={primary[100]} height="22px" width="16px" />}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.REQUESTED]: ({ isDisabled, onClick }) => (
    <SecondaryCTAButton
      display="Send reminder"
      isDisabled={isDisabled}
      leftIcon={<Bell fill={primary[100]} height="16px" width="16px" />}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.INCOMPLETE]: ({ isDisabled, onClick }) => (
    <SecondaryCTAButton
      display="Download PDF"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.IN_PROGRESS]: ({ isDisabled, onClick }) => (
    <SecondaryCTAButton
      display="Send reminder"
      isDisabled={isDisabled}
      leftIcon={<Bell fill={primary[100]} height="16px" width="16px" />}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.DONE]: ({ isDisabled, onClick }) => (
    <SecondaryCTAButton
      display="Download PDF"
      isDisabled={isDisabled}
      onClick={onClick}
    />
  ),
  [SurveyResponseStatus.VOID]: () => null,
};

type SurveyListItemActionsProps = {
  isDisabled: boolean;
  onCTAClick: (survey: SurveyResponse, isPrimaryCTA: boolean) => void;
  onEditClick: (survey: SurveyResponse) => void;
  survey: SurveyResponse;
};

const SurveyListItemActions = ({
  isDisabled,
  onCTAClick,
  onEditClick,
  survey,
}: SurveyListItemActionsProps) => (
  <ChakraFlex alignItems="center">
    {shouldShowEditOption(
      survey.toUserEmail,
      survey.status as SurveyResponseStatus,
    ) && (
      <ChakraBox marginRight="8px">
        <IconButton
          ariaLabel="Edit Assessment Respondent"
          icon={<Edit fill={primary[100]} />}
          id="survey-list-item-actions__edit-survey-respondent"
          isDisabled={isDisabled}
          onClick={() => onEditClick(survey)}
          size="sm"
          variant="outline"
        />
      </ChakraBox>
    )}
    {StatusToSecondaryCTAMapper[survey.status]?.({
      isDisabled,
      onClick: () => onCTAClick(survey, false),
      survey,
    })}
    {StatusToPrimaryCTAMapper[survey.status]?.({
      isDisabled,
      onClick: () => onCTAClick(survey, true),
    })}
  </ChakraFlex>
);

export default SurveyListItemActions;
